import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { APP_META } from '../shared/constants/themes';
import { DOMAINS } from '../shared/constants/domains';
import { getAggregateStats } from '../shared/engine/progressAggregator';
import { getStreakData } from '../shared/engine/streakTracker';
import { getStudyRecommendation } from '../shared/engine/studyRecommender';
import StatsBanner from './StatsBanner';
import StreakDisplay from './StreakDisplay';

/**
 * Suite landing page with stats, streak, recommendations, and app cards.
 */
export default function LandingPage() {
  const stats = useMemo(() => getAggregateStats(), []);
  const streak = useMemo(() => getStreakData(), []);
  const recommendation = useMemo(() => getStudyRecommendation(), []);

  const hasActivity = stats.totalCardsStudied > 0 || stats.examsCompleted > 0 || stats.drillSessions > 0;

  return (
    <div className="px-4 py-8 md:py-12">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
          CCNP ENCOR <span className="text-blue-400">350-401</span>
        </h1>
        <p className="text-gray-400 text-lg">Training Suite</p>
      </div>

      {/* Stats Banner (only shown if user has activity) */}
      {hasActivity && <StatsBanner stats={stats} />}

      {/* Streak */}
      <StreakDisplay currentStreak={streak.currentStreak} longestStreak={streak.longestStreak} />

      {/* Study Recommendation */}
      {hasActivity && recommendation.domain && (
        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4 flex items-start gap-3">
            <span className="text-xl">💡</span>
            <div>
              <div className="text-sm text-blue-300 font-medium">Today's Focus</div>
              <div className="text-gray-300 text-sm mt-1">
                Focus on <strong style={{ color: recommendation.domain.color }}>
                  {recommendation.domain.name}
                </strong> — readiness is {recommendation.domain.readiness}% in a domain
                worth {Math.round(recommendation.domain.weight * 100)}% of the exam.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* App Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto mb-8">
        {APP_META.map((app) => (
          <Link
            key={app.id}
            to={app.path}
            className="group block p-6 rounded-xl bg-gray-800/50 border border-gray-700/50 hover:border-blue-500/50 hover:bg-gray-800 transition-all duration-200"
          >
            <div className="flex items-start gap-4">
              <span className="text-3xl">{app.icon}</span>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-white group-hover:text-blue-400 transition-colors">
                  {app.name}
                </h2>
                <p className="text-gray-400 text-sm mt-1">{app.description}</p>
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {app.dailyTime}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Progress link */}
      <div className="text-center mb-8 flex flex-col sm:flex-row items-center justify-center gap-4">
        <Link
          to="/progress"
          className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm"
        >
          📊 View Full Progress Dashboard →
        </Link>
        <a
          href="/user-guide.html"
          className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm"
        >
          📖 How to Use This Suite (SE Guide) →
        </a>
      </div>

      {/* Domain Overview */}
      <div className="max-w-4xl mx-auto">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4 text-center">
          ENCOR Exam Domains
        </h3>
        <div className="flex flex-wrap justify-center gap-3">
          {DOMAINS.map((domain) => (
            <div
              key={domain.id}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800/30 border border-gray-700/30"
            >
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: domain.color }} />
              <span className="text-sm text-gray-300">{domain.name}</span>
              <span className="text-xs text-gray-500">{Math.round(domain.weight * 100)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
